<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStoreGetters } from 'dashboard/composables/store';
import { useInbox } from 'dashboard/composables/useInbox';

// Read-only. Shows the current conversation's WhatsApp/Evolution Gateway provider
// details — no editing, no messaging logic, purely a display of data already present
// in the store (inbox + the conversation's own messages). Deliberately does NOT make
// its own live gateway call on open (every conversation view would multiply gateway
// requests) — connection status is read from whatever the Communication Connection
// pages have already fetched into the shared store this session; falls back to
// "Unknown" rather than a live call, per the "do not poll aggressively" constraint.
const props = defineProps({
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const { t } = useI18n();
const { inbox } = useInbox(props.inboxId);
const getters = useStoreGetters();

const currentChat = useMapGetter('getSelectedChat');

const knownAccounts = computed(
  () => getters['communicationConnection/getAccounts'].value || []
);
const matchingAccount = computed(() =>
  knownAccounts.value.find(a => a.phone_number === inbox.value?.phoneNumber)
);
const connectionStatus = computed(
  () => matchingAccount.value?.connection_status || null
);

const lastOutgoingMessage = computed(() => {
  const messages = currentChat.value?.messages || [];
  return [...messages].reverse().find(m => m.message_type === 1 && m.source_id);
});

const lastDeliveryStatus = computed(
  () => lastOutgoingMessage.value?.status || null
);
const isRead = computed(() => lastDeliveryStatus.value === 'read');
</script>

<template>
  <div class="p-4 text-sm">
    <dl class="grid grid-cols-2 gap-y-3 gap-x-2">
      <dt class="text-n-slate-10">
        {{ t('COMMUNICATION_CONNECTION.CONVERSATION_PANEL.PROVIDER') }}
      </dt>
      <dd class="text-n-slate-12 text-end">
        {{ t('COMMUNICATION_CONNECTION.CONVERSATION_PANEL.PROVIDER_VALUE') }}
      </dd>

      <dt class="text-n-slate-10">
        {{ t('COMMUNICATION_CONNECTION.CONVERSATION_PANEL.PHONE_NUMBER') }}
      </dt>
      <dd class="text-n-slate-12 text-end">{{ inbox?.phoneNumber || '—' }}</dd>

      <dt class="text-n-slate-10">
        {{ t('COMMUNICATION_CONNECTION.CONVERSATION_PANEL.CONNECTION_STATUS') }}
      </dt>
      <dd class="text-end">
        <span
          v-if="connectionStatus"
          class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
          :class="
            connectionStatus === 'open'
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-ruby-3 text-n-ruby-11'
          "
        >
          {{ connectionStatus }}
        </span>
        <span v-else class="text-n-slate-10">{{
          t('COMMUNICATION_CONNECTION.GATEWAY_STATUS.UNKNOWN')
        }}</span>
      </dd>

      <template v-if="lastOutgoingMessage">
        <dt class="text-n-slate-10">
          {{
            t('COMMUNICATION_CONNECTION.CONVERSATION_PANEL.MESSAGE_SOURCE_ID')
          }}
        </dt>
        <dd class="text-n-slate-12 text-end break-all">
          {{ lastOutgoingMessage.source_id }}
        </dd>

        <dt class="text-n-slate-10">
          {{
            t(
              'COMMUNICATION_CONNECTION.CONVERSATION_PANEL.LAST_DELIVERY_STATUS'
            )
          }}
        </dt>
        <dd class="text-n-slate-12 text-end capitalize">
          {{ lastDeliveryStatus || '—' }}
        </dd>

        <dt class="text-n-slate-10">
          {{ t('COMMUNICATION_CONNECTION.CONVERSATION_PANEL.READ_STATUS') }}
        </dt>
        <dd class="text-end">
          <span
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
            :class="
              isRead
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-slate-3 text-n-slate-11'
            "
          >
            {{
              isRead
                ? t('COMMUNICATION_CONNECTION.CONVERSATION_PANEL.READ')
                : t('COMMUNICATION_CONNECTION.CONVERSATION_PANEL.NOT_READ')
            }}
          </span>
        </dd>
      </template>
      <p v-else class="col-span-2 text-n-slate-10 text-xs mt-1">
        {{ t('COMMUNICATION_CONNECTION.CONVERSATION_PANEL.NO_MESSAGES') }}
      </p>
    </dl>
  </div>
</template>
